import { queryOne, queryAll } from '../core/dom.js';

const GAME_NAMES = {
  chess: 'Cờ vua',
  xiangqi: 'Cờ tướng',
  caro: 'Cờ ca-rô',
  tycoon: 'Cờ tỷ phú'
};

const STATUS_LABELS = {
  waiting: 'Đang chờ',
  active: 'Đang chơi',
  finished: 'Đã kết thúc',
  abandoned: 'Gián đoạn',
  closed: 'Đã đóng'
};

function hostTokenStorageKey(gameType, roomCode) {
  return `ivs-room-host-${gameType}-${roomCode}`;
}

function normalizeRoom(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const gameType = String(raw.gameType || '');
  const roomCode = String(raw.roomCode || '').toUpperCase();
  if (!GAME_NAMES[gameType] || !roomCode) return null;
  const maxPlayers = Number(raw.maxPlayers) || (gameType === 'tycoon' ? 4 : 2);
  const occupiedPlayers = Number(raw.occupiedPlayers ?? raw.occupiedSeats) || 0;
  return {
    roomCode,
    gameType,
    status: String(raw.status || 'waiting'),
    discoverable: raw.discoverable === true,
    allowSpectators: raw.allowSpectators !== false,
    hasPassword: Boolean(raw.hasPassword),
    occupiedPlayers,
    maxPlayers,
    createdAt: Number(raw.createdAt) || 0,
    updatedAt: Number(raw.updatedAt) || 0
  };
}

function formatRoomAge(timestamp) {
  const value = Number(timestamp) || 0;
  if (!value) return '';
  const diff = Math.max(0, Date.now() - value);
  const minute = 60_000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < minute) return 'vừa cập nhật';
  if (diff < hour) return `${Math.floor(diff / minute)} phút trước`;
  if (diff < day) return `${Math.floor(diff / hour)} giờ trước`;
  try {
    return new Intl.DateTimeFormat('vi-VN', { day: '2-digit', month: '2-digit' }).format(new Date(value));
  } catch {
    return '';
  }
}

function createText(tag, className, value) {
  const el = document.createElement(tag);
  if (className) el.className = className;
  el.textContent = value;
  return el;
}

export class RoomDirectoryController {
  constructor({
    roomsUrl,
    fetchImpl = (...args) => fetch(...args),
    storage = globalThis.localStorage,
    locationObj = globalThis.location,
    root = null,
    selectors = {},
    defaultGame = 'chess',
    limit = 80,
    onEnterRoom = () => {},
    onChangeGame = () => {},
    onNotice = () => {},
    onMutated = () => {}
  } = {}) {
    this.roomsUrl = roomsUrl;
    this.fetchImpl = fetchImpl;
    this.storage = storage;
    this.locationObj = locationObj;
    this.root = typeof root === 'string' ? queryOne(root) : root;
    this.selectors = {
      gameFilter: '#roomGameFilter',
      statusFilter: '#roomStatusFilter',
      refresh: '#refreshRoomsBtn',
      list: '#roomDirectoryList',
      count: '#roomDirectoryCount',
      ...selectors
    };
    this.onEnterRoom = onEnterRoom;
    this.onChangeGame = onChangeGame;
    this.onNotice = onNotice;
    this.onMutated = onMutated;
    this.scope = 'all';
    this.currentGame = GAME_NAMES[defaultGame] ? defaultGame : '';
    this.filterTouched = false;
    this.loading = false;
    this.pendingClose = null;
    this.limit = Math.max(1, Math.min(80, Number(limit) || 80));
    this.mounted = false;
  }

  one(selector) {
    if (!selector) return null;
    return this.root?.querySelector(selector) || queryOne(selector);
  }

  all(selector) {
    if (!selector) return [];
    return this.root ? [...this.root.querySelectorAll(selector)] : queryAll(selector);
  }

  mount() {
    if (this.mounted) return;
    this.mounted = true;

    this.all('[data-room-scope]').forEach(button => {
      button.addEventListener('click', () => {
        this.scope = button.dataset.roomScope === 'mine' ? 'mine' : 'all';
        this.all('[data-room-scope]').forEach(item => {
          const active = item === button;
          item.classList.toggle('active', active);
          item.setAttribute('aria-selected', active ? 'true' : 'false');
        });
        void this.refresh();
      });
    });

    this.one(this.selectors.gameFilter)?.addEventListener('change', () => {
      this.filterTouched = true;
      void this.refresh();
    });
    this.one(this.selectors.statusFilter)?.addEventListener('change', () => void this.refresh());
    this.one(this.selectors.refresh)?.addEventListener('click', () => void this.refresh());

    // One shared confirmation dialog is intentionally reused by both the
    // home directory and the in-lobby management view. Only the controller
    // with a pending room acts on confirmation.
    queryOne('#cancelCloseRoomBtn')?.addEventListener('click', () => {
      this.pendingClose = null;
      queryOne('#closeManagedRoomDialog')?.close();
    });
    queryOne('#confirmCloseManagedRoomBtn')?.addEventListener('click', () => void this.confirmCloseRoom());
  }

  setCurrentGame(gameType) {
    if (!GAME_NAMES[gameType]) return;
    this.currentGame = gameType;
    const filter = this.one(this.selectors.gameFilter);
    if (filter && !this.filterTouched) filter.value = gameType;
  }

  getHostToken(gameType, roomCode) {
    try {
      return this.storage?.getItem(hostTokenStorageKey(gameType, roomCode)) || '';
    } catch {
      return '';
    }
  }

  removeHostToken(gameType, roomCode) {
    try {
      this.storage?.removeItem(hostTokenStorageKey(gameType, roomCode));
    } catch {}
  }

  hostRoomRefs() {
    const refs = [];
    if (!this.storage) return refs;
    try {
      for (let index = 0; index < this.storage.length; index++) {
        const key = this.storage.key(index);
        const match = key?.match(/^ivs-room-host-(chess|xiangqi|caro|tycoon)-([A-Z0-9_-]{4,12})$/);
        if (!match) continue;
        refs.push({ gameType: match[1], roomCode: match[2], token: this.storage.getItem(key) || '' });
      }
    } catch {}
    return refs;
  }

  filters() {
    const gameFilter = this.one(this.selectors.gameFilter);
    const statusFilter = this.one(this.selectors.statusFilter);
    return {
      // Preserve the explicit empty value: it means "Tất cả trò chơi".
      game: gameFilter ? gameFilter.value : this.currentGame,
      status: statusFilter ? statusFilter.value : ''
    };
  }

  async loadPublicRooms() {
    const { game, status } = this.filters();
    const url = new URL(this.roomsUrl);
    if (game) url.searchParams.set('game', game);
    if (status) url.searchParams.set('status', status);
    url.searchParams.set('limit', String(this.limit));

    const response = await this.fetchImpl(url.toString(), { headers: { Accept: 'application/json' } });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Không thể tải danh sách phòng');

    return (Array.isArray(result.rooms) ? result.rooms : [])
      .map(normalizeRoom)
      .filter(Boolean)
      .map(room => ({ ...room, isMine: Boolean(this.getHostToken(room.gameType, room.roomCode)) }));
  }

  async loadMyRooms() {
    const { game, status } = this.filters();
    const refs = this.hostRoomRefs().filter(ref => !game || ref.gameType === game);
    const rooms = [];

    await Promise.all(refs.slice(0, this.limit).map(async ref => {
      try {
        const url = new URL(`${this.roomsUrl}/${encodeURIComponent(ref.roomCode)}`);
        url.searchParams.set('game', ref.gameType);
        const response = await this.fetchImpl(url.toString(), { headers: { Accept: 'application/json' } });
        if (response.status === 404 || response.status === 410) {
          this.removeHostToken(ref.gameType, ref.roomCode);
          return;
        }
        const result = await response.json();
        if (!response.ok) return;
        const room = normalizeRoom(result);
        if (!room || (status && room.status !== status)) return;
        rooms.push({ ...room, isMine: true });
      } catch {
        // Keep locally known rooms on transient network failures.
      }
    }));

    rooms.sort((left, right) => (right.updatedAt || right.createdAt) - (left.updatedAt || left.createdAt));
    return rooms;
  }

  async refresh({ quiet = false } = {}) {
    if (this.loading) return;
    this.loading = true;
    const list = this.one(this.selectors.list);
    const count = this.one(this.selectors.count);
    const refresh = this.one(this.selectors.refresh);

    if (!quiet && list) {
      list.replaceChildren(createText('div', 'room-directory-state', 'Đang tải danh sách phòng...'));
    }
    if (!quiet && count) count.textContent = 'Đang tải';
    if (refresh) {
      refresh.disabled = true;
      refresh.setAttribute('aria-busy', 'true');
    }

    try {
      const rooms = this.scope === 'mine' ? await this.loadMyRooms() : await this.loadPublicRooms();
      this.render(rooms);
      return rooms;
    } catch (error) {
      const rawMessage = String(error?.message || '');
      const isNetworkFailure = error?.name === 'TypeError'
        || /failed to fetch|networkerror|load failed/i.test(rawMessage);
      const message = isNetworkFailure
        ? 'Không thể kết nối máy chủ LIVE. Kiểm tra kết nối mạng rồi thử lại.'
        : (rawMessage || 'Không thể tải danh sách phòng.');

      if (!quiet && list) {
        list.replaceChildren(createText('div', 'room-directory-state error', message));
      }
      if (!quiet && count) count.textContent = 'Lỗi tải';
      return [];
    } finally {
      this.loading = false;
      if (refresh) {
        refresh.disabled = false;
        refresh.removeAttribute('aria-busy');
      }
    }
  }

  render(rooms) {
    const list = this.one(this.selectors.list);
    const count = this.one(this.selectors.count);
    if (!list) return;

    if (count) count.textContent = `${rooms.length} phòng`;
    list.replaceChildren();

    if (!rooms.length) {
      const message = this.scope === 'mine'
        ? 'Thiết bị này chưa có phòng đã tạo còn hoạt động.'
        : 'Chưa có phòng phù hợp với bộ lọc.';
      list.appendChild(createText('div', 'room-directory-state', message));
      return;
    }

    for (const room of rooms) list.appendChild(this.renderRoomCard(room));
  }

  renderRoomCard(room) {
    const card = document.createElement('article');
    card.className = `room-list-card status-${room.status}`;

    const top = document.createElement('div');
    top.className = 'room-list-card-top';

    const identity = document.createElement('div');
    identity.className = 'room-list-identity';
    identity.appendChild(createText('span', 'room-game-name', GAME_NAMES[room.gameType]));
    identity.appendChild(createText('strong', 'room-code', room.roomCode));

    const status = createText('span', `room-status-badge status-${room.status}`, STATUS_LABELS[room.status] || room.status);
    top.append(identity, status);

    const meta = document.createElement('div');
    meta.className = 'room-list-meta';
    meta.appendChild(createText('span', '', `👥 ${room.occupiedPlayers}/${room.maxPlayers}`));
    meta.appendChild(createText('span', '', room.hasPassword ? '🔒 Có mật khẩu' : '🔓 Không mật khẩu'));
    meta.appendChild(createText('span', '', room.allowSpectators ? '👁 Cho phép xem' : '🚫 Không khán giả'));
    meta.appendChild(createText('span', '', room.discoverable ? '🌐 Công khai' : '🙈 Không công khai'));
    if (room.updatedAt) meta.appendChild(createText('span', 'room-list-freshness', `🕒 ${formatRoomAge(room.updatedAt)}`));
    if (room.isMine) meta.appendChild(createText('span', 'room-owner-chip', 'Phòng của tôi'));

    const actions = document.createElement('div');
    actions.className = 'room-list-actions';

    const enter = createText('button', 'btn primary room-card-action', room.status === 'finished' ? 'Mở phòng' : 'Vào phòng');
    enter.type = 'button';
    enter.addEventListener('click', () => this.onEnterRoom(room));
    actions.appendChild(enter);

    const copy = createText('button', 'btn room-card-action', 'Sao chép link');
    copy.type = 'button';
    copy.addEventListener('click', () => this.copyRoomLink(room, copy));
    actions.appendChild(copy);

    if (room.isMine) {
      if (room.status === 'waiting') {
        const changeGame = createText('button', 'btn room-card-action', 'Đổi game');
        changeGame.type = 'button';
        changeGame.addEventListener('click', () => this.onChangeGame(room));
        actions.appendChild(changeGame);
      }

      const close = createText('button', 'btn danger-outline room-card-action', 'Đóng phòng');
      close.type = 'button';
      close.addEventListener('click', () => this.requestCloseRoom(room));
      actions.appendChild(close);
    }

    card.append(top, meta, actions);
    return card;
  }

  async copyRoomLink(room, button) {
    const inviteUrl = new URL(this.locationObj.href);
    inviteUrl.search = '';
    inviteUrl.searchParams.set('game', room.gameType);
    inviteUrl.searchParams.set('room', room.roomCode);
    try {
      await navigator.clipboard.writeText(inviteUrl.toString());
      const old = button.textContent;
      button.textContent = 'Đã sao chép';
      setTimeout(() => { button.textContent = old; }, 1200);
    } catch {
      this.onNotice('Không thể sao chép tự động. Hãy sao chép URL trên trình duyệt.');
    }
  }

  requestCloseRoom(room) {
    const token = this.getHostToken(room.gameType, room.roomCode);
    if (!token) {
      this.onNotice('Không tìm thấy quyền chủ phòng trên thiết bị này.');
      return;
    }
    this.pendingClose = { room, token };
    const code = queryOne('#closeManagedRoomCode');
    if (code) code.textContent = room.roomCode;
    queryOne('#closeManagedRoomDialog')?.showModal();
  }

  async confirmCloseRoom() {
    if (!this.pendingClose) return;
    const { room, token } = this.pendingClose;
    const button = queryOne('#confirmCloseManagedRoomBtn');
    if (button) {
      button.disabled = true;
      button.textContent = 'Đang đóng...';
    }

    try {
      const url = new URL(`${this.roomsUrl}/${encodeURIComponent(room.roomCode)}`);
      url.searchParams.set('game', room.gameType);
      const response = await this.fetchImpl(url.toString(), {
        method: 'DELETE',
        headers: { 'X-IVS-Host-Token': token, Accept: 'application/json' }
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Không thể đóng phòng');
      this.removeHostToken(room.gameType, room.roomCode);
      this.pendingClose = null;
      queryOne('#closeManagedRoomDialog')?.close();
      this.onNotice(`Đã đóng phòng ${room.roomCode}.`);
      this.onMutated(room);
      await this.refresh();
    } catch (error) {
      this.onNotice(error.message || 'Không thể đóng phòng.');
    } finally {
      if (button) {
        button.disabled = false;
        button.textContent = 'Đóng phòng';
      }
    }
  }
}
